import { Paper } from '@mantine/core';
import React, { useEffect, useRef } from 'react';
import { useEditorStore } from '../store/editorStore';
import { DraggableItem } from './DraggableItem';

// Canvas dimensions
const CANVAS_WIDTH = 1280;
const CANVAS_HEIGHT = 720;
const CANVAS_SCALE = 0.6; // Scale for display

export function Editor() {
  const items = useEditorStore((state) => state.items);
  const selectItem = useEditorStore((state) => state.selectItem);
  const selectedItemId = useEditorStore((state) => state.selectedItemId);
  const canvasRef = useRef<HTMLDivElement>(null);

  // Xử lý click vào canvas để bỏ chọn item
  const handleCanvasClick = (e: React.MouseEvent) => {
    // Chỉ bỏ chọn khi click trực tiếp vào canvas, không phải vào các item con
    if (e.currentTarget === e.target) {
      selectItem(null);
    }
  };

  // Thêm event listener toàn cục để bỏ chọn khi click ra ngoài canvas
  useEffect(() => {
    const handleGlobalClick = (e: MouseEvent) => {
      // Chỉ thực hiện khi đang có item được chọn
      if (!selectedItemId) return;
      
      // Kiểm tra xem click có xảy ra bên ngoài canvas không
      if (canvasRef.current && !canvasRef.current.contains(e.target as Node)) {
        selectItem(null);
      }
    };

    // Thêm event listener
    document.addEventListener('mousedown', handleGlobalClick);

    // Cleanup khi component unmount
    return () => {
      document.removeEventListener('mousedown', handleGlobalClick);
    };
  }, [selectedItemId, selectItem]);

  // Ngăn chặn hành vi kéo thả mặc định của trình duyệt
  useEffect(() => {
    const handleDragStart = (e: DragEvent) => {
      e.preventDefault();
    };

    // Thêm event listener để chặn drag mặc định
    document.addEventListener('dragstart', handleDragStart);

    return () => {
      document.removeEventListener('dragstart', handleDragStart);
    };
  }, []);

  const scaledCanvasStyle = {
    width: CANVAS_WIDTH * CANVAS_SCALE,
    height: CANVAS_HEIGHT * CANVAS_SCALE,
    position: 'relative' as const,
    overflow: 'hidden' as const,
    backgroundColor: '#f0f0f0',
    margin: '0 auto',
    boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
    touchAction: 'none', // Ngăn chặn các hành động cảm ứng mặc định
  };

  const containerStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px',
    minHeight: `${CANVAS_HEIGHT * CANVAS_SCALE + 40}px`,
    flex: 1,
    overflow: 'hidden', // Ngăn chặn scroll khi kéo thả ra ngoài
    position: 'relative' as const,
    touchAction: 'none', // Ngăn chặn các hành động cảm ứng mặc định
  };

  // Sắp xếp các item theo zIndex để hiển thị đúng thứ tự
  const sortedItems = [...items].sort((a, b) => a.zIndex - b.zIndex);

  return (
    <div style={containerStyle}>
      <Paper
        ref={canvasRef}
        shadow="sm"
        p={0}
        onClick={handleCanvasClick}
        style={scaledCanvasStyle}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => e.preventDefault()}
      >
        <div style={{ position: 'absolute', bottom: 5, right: 5, fontSize: 10, color: '#aaa' }}>
          {CANVAS_WIDTH} x {CANVAS_HEIGHT}
        </div>

        {sortedItems.map((item) => {
          // Process variables in text content
          let processedContent = item.content;
          
          return (
            <DraggableItem
              key={item.id}
              item={{
                ...item,
                content: processedContent
              }}
              isSelected={item.id === selectedItemId}
              scale={CANVAS_SCALE}
              canvasWidth={CANVAS_WIDTH}
              canvasHeight={CANVAS_HEIGHT}
              onSelect={() => selectItem(item.id)}
            />
          );
        })}
      </Paper>
    </div>
  );
} 